import React from 'react';

import { Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import { useUIContext } from 'state/ui';
import { platform } from 'utils/environment';

import { useActiveTieredDeliveryFees } from '../hooks/use-active-tiered-delivery-fees';

import FreeDeliveryProgress from './free-delivery-progress';

const FreeDeliveryProgressContainer: React.FC<React.PropsWithChildren<{
  serverOrder: IServerOrder | null;
  isLoading: boolean;
}>> = ({ serverOrder, isLoading }) => {
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const subTotal = serverOrder?.cart.subTotalCents ?? 0;
  const { freeDeliveryThreshold } = useActiveTieredDeliveryFees({
    subTotalCents: subTotal,
    platform: platform(),
  });

  const shouldShowFreeDeliveryBar = !!freeDeliveryThreshold;

  if (!shouldShowFreeDeliveryBar) {
    return null;
  }

  const freeDeliveryPercentageComplete = Math.min(subTotal / freeDeliveryThreshold, 1) * 100;
  const amountRemaining = freeDeliveryThreshold - subTotal;

  return (
    <FreeDeliveryProgress
      isLoading={isLoading}
      isFreeDelivery={freeDeliveryPercentageComplete === 100}
      freeDeliveryPercentageComplete={freeDeliveryPercentageComplete}
      titleLabel={formatMessage(
        { id: 'freeDeliveryOverAmount' },
        {
          amount: formatCurrencyForLocale(freeDeliveryThreshold),
        }
      )}
      freeDeliveryReachedLabel={formatMessage({ id: 'youQualifyFreeDelivery' })}
      freeDeliveryInProgressLabel={formatMessage(
        { id: 'youreXAwayFromFreeDelivery' },
        {
          amount: <Text fontWeight="bold">{formatCurrencyForLocale(amountRemaining)}</Text>,
        }
      )}
    />
  );
};

export default FreeDeliveryProgressContainer;
